import React from 'react';
import { K } from '../constants';
import { calcFL } from '../utils/formulas';
import { NI, Sel, Lbl, SecHd, Metric, MathBox } from '../components/ui';
import { usePersist } from '../hooks/usePersist';

const CUSTOM_DEFAULTS = { mode: 'fl', c: 15.5, gpm: 150, len: 200, elevFt: 50, eMode: 'cm', np: 50, fl: 0, app: 0, pElev: 0 };

export function CustomCalc({ showMath, calcDefaults }) {
  const elevFactor = calcDefaults?.ops?.elevFactor ?? 0.5;
  const [s, setS] = usePersist('hydro_custom_inputs', CUSTOM_DEFAULTS);
  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));
  const { mode, c, gpm, len, elevFt, eMode, np, fl, app, pElev } = { ...CUSTOM_DEFAULTS, ...s };
  const setMode = (v) => set('mode', v);
  const setC = (v) => set('c', v);
  const setGpm = (v) => set('gpm', v);
  const setLen = (v) => set('len', v);
  const setElevFt = (v) => set('elevFt', v);
  const setEMode = (v) => set('eMode', v);
  const setNp = (v) => set('np', v);
  const setFl = (v) => set('fl', v);
  const setApp = (v) => set('app', v);
  const setPElev = (v) => set('pElev', v);

  const flR = calcFL(c, gpm, len);
  const factor = eMode === 'cm' ? 0.4 : eMode === 'ifsta' ? 0.433 : elevFactor;
  const elevR = factor * elevFt;
  const pdp = np + fl + app + elevFactor * pElev;

  return (
    <div>
      <SecHd title="Quick Calc" sub="For the weird one-off problem when you do not want to fight the full builder." />
      <div className="hc-grid-3" style={{ gap: 6, marginBottom: 20 }}>
        {[['fl', 'Friction Loss'], ['elev', 'Elevation'], ['pdp', 'Build PDP']].map(([id, label]) => (
          <button key={id} onClick={() => setMode(id)} style={{ background: mode === id ? K.red : K.bg2, border: `1px solid ${mode === id ? K.red2 : K.bdr}`, color: mode === id ? K.t0 : K.t1, padding: '12px 6px', borderRadius: 8, cursor: 'pointer', fontFamily: K.fSans, fontWeight: 600 }}>{label}</button>
        ))}
      </div>
      {mode === 'fl' ? (
        <>
          <div className="hc-grid-3" style={{ marginBottom: 14 }}>
            <div><Lbl t="C" /><NI v={c} set={setC} min={0} max={100} step={0.001} dec /></div>
            <div><Lbl t="GPM" /><NI v={gpm} set={setGpm} min={0} max={5000} /></div>
            <div><Lbl t="Length (ft)" /><NI v={len} set={setLen} min={0} max={10000} /></div>
          </div>
          <Metric label="Friction Loss" value={`${flR.toFixed(2)} psi`} col={K.gold} />
          <MathBox show={showMath} steps={[{ l: 'FL', f: `${c}×(${gpm}/100)²×(${len}/100)`, r: `${flR.toFixed(2)} psi` }]} />
        </>
      ) : null}
      {mode === 'elev' ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
            <div><Lbl t="Elevation (ft)" /><NI v={elevFt} set={setElevFt} min={-1000} max={1000} /></div>
            <div><Lbl t="Method" /><Sel v={eMode} set={setEMode}><option value="std">Standard — 0.5 psi/ft</option><option value="ifsta">IFSTA — 0.433 psi/ft</option><option value="cm">CMFE — 0.4 psi/ft</option></Sel></div>
          </div>
          <Metric label={elevFt >= 0 ? 'Back Pressure' : 'Forward Pressure'} value={`${elevR > 0 ? '+' : ''}${elevR.toFixed(2)} psi`} col={elevFt >= 0 ? K.bad : K.ok} />
          <MathBox show={showMath} steps={[{ l: 'Elevation', f: `${factor}×${elevFt}`, r: `${elevR.toFixed(2)} psi` }]} />
        </>
      ) : null}
      {mode === 'pdp' ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
            <div><Lbl t="NP (psi)" /><NI v={np} set={setNp} min={0} max={400} /></div>
            <div><Lbl t="Friction Loss (psi)" /><NI v={fl} set={setFl} min={0} max={500} /></div>
            <div><Lbl t="Appliance Loss (psi)" /><NI v={app} set={setApp} min={0} max={200} /></div>
            <div><Lbl t="Elevation (ft)" /><NI v={pElev} set={setPElev} min={-500} max={500} /></div>
          </div>
          <Metric label="Required PDP" value={`${pdp.toFixed(1)} psi`} col={K.gold} />
          <MathBox show={showMath} steps={[{ l: 'PDP', f: `${np}+${fl}+${app}+(0.5×${pElev})`, r: `${pdp.toFixed(1)} psi` }]} />
        </>
      ) : null}
    </div>
  );
}
